import React, { useEffect, useMemo, useState } from 'react';

/**
 * Public, OTP-gated page where a doctor / provider verifies an ESA/SA.
 *
 * Reached via the emailed link /attest?token=<token>. The provider requests a
 * one-time code (sent to their email), enters it, reviews the resident's info
 * and documents, then attests. No portal login is involved.
 *
 * Documents are rendered inline (in-page) rather than opened in a new tab so
 * the short-lived presigned URLs are never surfaced in the address bar or left
 * lingering in a separate tab.
 */

type Phase = 'otp' | 'review' | 'done' | 'invalid';

interface AttestationDoc {
  id: number;
  name: string;
  document_type?: string;
  mime_type?: string;
  url?: string | null;
}

interface AttestationData {
  already_attested: boolean;
  resident: {
    name?: string;
    email?: string;
    phone?: string;
    community?: string;
    unit?: string;
  };
  pet: Record<string, any>;
  documents: AttestationDoc[];
}

const apiBase = (): string =>
  (typeof window !== 'undefined' && window.location.hostname === 'localhost')
    ? 'http://localhost:8000/api/'
    : (import.meta.env.VITE_API_URL || 'https://pawtected-client-dev-b941ac1d80e9.herokuapp.com/api/');

const roleLabel = (role?: string): string => {
  if (role === 'esa') return 'Emotional Support Animal';
  if (role === 'sa') return 'Service Animal';
  return role || 'Animal';
};

const isImageDoc = (d: AttestationDoc): boolean =>
  (d.mime_type || '').startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(d.name || '');

const isPdfDoc = (d: AttestationDoc): boolean =>
  d.mime_type === 'application/pdf' || /\.pdf(\?|$)/i.test(d.name || '');

const AttestationPage: React.FC = () => {
  const token = useMemo(() => new URLSearchParams(window.location.search).get('token') || '', []);

  const [phase, setPhase] = useState<Phase>('otp');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [info, setInfo] = useState<string>('');

  const [otpRequested, setOtpRequested] = useState<boolean>(false);
  const [emailMasked, setEmailMasked] = useState<string>('');
  const [code, setCode] = useState<string>('');
  const [sessionToken, setSessionToken] = useState<string>('');
  const [data, setData] = useState<AttestationData | null>(null);
  const [activeDocId, setActiveDocId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) setPhase('invalid');
  }, [token]);

  const requestOtp = async () => {
    setError('');
    setInfo('');
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase()}attestation/request-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        if (resp.status === 409) {
          setInfo('This animal has already been verified. Thank you!');
          setPhase('done');
          return;
        }
        throw new Error(body.detail || 'Could not send a code. Please try again.');
      }
      setEmailMasked(body.email_masked || '');
      setOtpRequested(true);
      setInfo(`We sent a 6-digit code to ${body.email_masked || 'your email'}. It expires in ${body.expires_in_minutes || 5} minutes.`);
    } catch (e: any) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase()}attestation/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code: code.trim() }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.detail || 'Invalid code.');
      setSessionToken(body.session_token);
      await loadPet(body.session_token);
    } catch (e: any) {
      setError(e.message || 'Invalid code.');
    } finally {
      setLoading(false);
    }
  };

  const loadPet = async (sess: string) => {
    const resp = await fetch(
      `${apiBase()}attestation/pet?token=${encodeURIComponent(token)}&session_token=${encodeURIComponent(sess)}`,
    );
    const body = await resp.json();
    if (!resp.ok) throw new Error(body.detail || 'Could not load the record.');
    setData(body);
    if (body.already_attested) {
      setInfo('This animal has already been verified. Thank you!');
      setPhase('done');
    } else {
      setPhase('review');
    }
  };

  const attest = async () => {
    setError('');
    setLoading(true);
    try {
      const resp = await fetch(`${apiBase()}attestation/attest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, session_token: sessionToken }),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(body.detail || 'Could not record your attestation.');
      setInfo('Thank you. Your attestation has been recorded.');
      setPhase('done');
    } catch (e: any) {
      setError(e.message || 'Could not record your attestation.');
    } finally {
      setLoading(false);
    }
  };

  const Card: React.FC<{ children: React.ReactNode; wide?: boolean }> = ({ children, wide }) => (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-indigo-100 flex items-center justify-center px-4 py-10">
      <div className={`w-full ${wide ? 'max-w-3xl' : 'max-w-xl'} bg-white rounded-3xl shadow-xl p-6 sm:p-10`}>
        <div className="flex flex-col items-center text-center mb-8">
          <img src="/landing/getpawtected.svg" alt="GetPawtected" className="w-52 sm:w-64 h-auto mb-4" />
          <p className="text-sm text-gray-500 font-inter">Assistance Animal Verification</p>
        </div>
        {error && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm p-3 font-inter">{error}</div>}
        {info && <div className="mb-4 rounded-xl bg-[color:var(--pmc-primary)]/10 border border-[color:var(--pmc-accent)]/30 text-[#1c6f93] text-sm p-3 font-inter">{info}</div>}
        {children}
      </div>
    </div>
  );

  const primaryBtn =
    'w-full bg-[color:var(--pmc-primary)] text-white font-medium font-poppins py-3.5 rounded-full hover:bg-[#FF6244] transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-[color:var(--pmc-accent)] focus:ring-offset-2';

  if (phase === 'invalid') {
    return (
      <Card>
        <p className="text-center text-gray-700 font-inter">This verification link is invalid or has expired.</p>
      </Card>
    );
  }

  if (phase === 'done') {
    return (
      <Card>
        <div className="text-center py-6">
          <div className="text-5xl mb-3">✅</div>
          <p className="text-gray-700 font-inter">{info || 'Attestation complete.'}</p>
          <p className="text-xs text-gray-400 mt-4 font-inter">You may now close this window.</p>
        </div>
      </Card>
    );
  }

  if (phase === 'otp') {
    return (
      <Card>
        <p className="text-gray-700 text-sm mb-5 font-inter leading-relaxed">
          To protect the resident's information, we'll email you a one-time code to confirm your identity before you review and verify this assistance animal.
        </p>
        {!otpRequested ? (
          <button onClick={requestOtp} disabled={loading} className={primaryBtn}>
            {loading ? 'Sending…' : 'Send me a code'}
          </button>
        ) : (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2 font-inter">Enter the 6-digit code</label>
              <input
                inputMode="numeric"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="w-full text-center tracking-[0.5em] text-2xl p-3 border-2 border-gray-300 rounded-xl focus:outline-none focus:border-[color:var(--pmc-accent)] transition-colors"
                placeholder="------"
              />
            </div>
            <button onClick={verifyOtp} disabled={loading || code.length !== 6} className={primaryBtn}>
              {loading ? 'Verifying…' : 'Verify & continue'}
            </button>
            <button
              onClick={requestOtp}
              disabled={loading}
              className="w-full text-[color:var(--pmc-accent)] text-sm hover:underline font-inter disabled:opacity-50"
            >
              Resend code
            </button>
          </div>
        )}
      </Card>
    );
  }

  // review
  const pet = data?.pet || {};
  const resident = data?.resident || {};
  const activeDoc = data?.documents.find((d) => d.id === activeDocId) || null;
  return (
    <Card wide>
      <div className="space-y-6">
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 font-inter">Resident</h2>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm font-inter">
            <div className="text-gray-500">Name</div><div className="text-gray-900">{resident.name || '—'}</div>
            <div className="text-gray-500">Email</div><div className="text-gray-900">{resident.email || '—'}</div>
            <div className="text-gray-500">Phone</div><div className="text-gray-900">{resident.phone || '—'}</div>
            <div className="text-gray-500">Community</div><div className="text-gray-900">{resident.community || '—'}</div>
            <div className="text-gray-500">Unit</div><div className="text-gray-900">{resident.unit || '—'}</div>
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 font-inter">Animal</h2>
          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm font-inter">
            <div className="text-gray-500">Classification</div><div className="text-gray-900">{roleLabel(pet.pet_role)}</div>
            <div className="text-gray-500">Name</div><div className="text-gray-900">{pet.pet_name || '—'}</div>
            <div className="text-gray-500">Type</div><div className="text-gray-900">{pet.pet_type || '—'}</div>
            <div className="text-gray-500">Breed</div><div className="text-gray-900">{pet.breed || '—'}</div>
            <div className="text-gray-500">Age</div><div className="text-gray-900">{pet.age ?? '—'}</div>
            {pet.esa_professional && (<><div className="text-gray-500">Provider</div><div className="text-gray-900">{pet.esa_professional}</div></>)}
            {pet.esa_letter_date && (<><div className="text-gray-500">Letter date</div><div className="text-gray-900">{pet.esa_letter_date}</div></>)}
            {pet.sa_tasks_trained && (<><div className="text-gray-500">Trained tasks</div><div className="text-gray-900">{pet.sa_tasks_trained}</div></>)}
          </div>
        </section>

        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 font-inter">Documents</h2>
          {data && data.documents.length > 0 ? (
            <>
              <ul className="space-y-2">
                {data.documents.map((d) => {
                  const isActive = d.id === activeDocId;
                  return (
                    <li key={d.id} className="flex items-center justify-between border border-gray-200 rounded-xl p-3 text-sm font-inter">
                      <span className="text-gray-800">
                        {d.name} {d.document_type ? <span className="text-gray-400">({d.document_type})</span> : null}
                      </span>
                      {d.url ? (
                        <button
                          onClick={() => setActiveDocId(isActive ? null : d.id)}
                          className="text-[color:var(--pmc-accent)] font-medium hover:underline focus:outline-none"
                        >
                          {isActive ? 'Hide' : 'View'}
                        </button>
                      ) : (
                        <span className="text-gray-400">Unavailable</span>
                      )}
                    </li>
                  );
                })}
              </ul>

              {activeDoc && activeDoc.url && (
                <div className="mt-4 border border-gray-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-700 truncate font-inter">{activeDoc.name}</span>
                    <button
                      onClick={() => setActiveDocId(null)}
                      className="text-gray-500 hover:text-gray-800 text-sm font-inter focus:outline-none"
                    >
                      Close
                    </button>
                  </div>
                  <div className="bg-gray-100 flex justify-center">
                    {isImageDoc(activeDoc) ? (
                      <img
                        src={activeDoc.url}
                        alt={activeDoc.name}
                        className="w-full max-h-[70vh] object-contain"
                      />
                    ) : (
                      <iframe
                        src={activeDoc.url}
                        title={activeDoc.name}
                        className="w-full h-[70vh] bg-white"
                      />
                    )}
                  </div>
                  {!isImageDoc(activeDoc) && !isPdfDoc(activeDoc) && (
                    <p className="text-xs text-gray-400 px-4 py-2 font-inter">
                      If this document type can't be previewed, ask the resident to re-upload it as a PDF or image.
                    </p>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="text-sm text-gray-500 font-inter">No documents were provided.</p>
          )}
        </section>

        <div className="border-t border-gray-200 pt-5">
          <p className="text-xs text-gray-500 mb-3 font-inter leading-relaxed">
            By clicking "Attest", you confirm that you are the provider associated with this assistance animal and that the information above is accurate to the best of your knowledge.
          </p>
          <button onClick={attest} disabled={loading} className={primaryBtn}>
            {loading ? 'Submitting…' : 'Attest'}
          </button>
        </div>
      </div>
    </Card>
  );
};

export default AttestationPage;
